/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { Modal, ModalHeader, ModalBody } from "baseui/modal";
import { Button } from "baseui/button";
import { gql, useQuery } from "@apollo/client";
import GetFileContents from "../Api/Gql/GetFileContents";
import { Component, FileType } from "../Api/Api";
import { FileContentPreview } from "./FileContentPreview";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { GeneralResponse, ProtectedUrl } from "../Api/Api";
import { AxiosContext } from "../Utils/AuthContext";
import { Endpoints } from "../Utils/Common";
import { SiteContext } from "../Utils/SiteProps";
import { loggedNotify } from "./Notify";
import { FaDownload } from "react-icons/fa";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { getTooltipOverrides } from "../DesignSystem/TooltipStyles";

export const NodeFileDataModal = ({
  isOpen,
  header,
  fileType,
  fileId,
  onClose,
  composerFileContent,
}: {
  isOpen: boolean;
  header: string;
  fileType: FileType;
  fileId?: string;
  onClose: () => void;
  composerFileContent?: string;
}): JSX.Element => {
  const { currentTheme } = useDexterTheme();
  const axiosContext = React.useContext(AxiosContext);
  const siteProps = React.useContext(SiteContext);

  const { loading, error, data } = useQuery(gql(GetFileContents), {
    variables: { id: fileId },
    // Don't query until opening modal
    skip: !isOpen || !!composerFileContent || !fileId, // skip if we have composerFileContent
  });

  const contentToShow = composerFileContent ?? data?.getFileContents;

  const handleDownload = React.useCallback(async () => {
    // Only ever called when fileId is present (button is gated on it —
    // composer nodes have no fileId, so they never reach this)
    try {
      const downloadFileTokenResponse = await axiosContext.get(
        `${Endpoints.DownloadFileToken}=${fileId}`
      );
      const tokenData: GeneralResponse = downloadFileTokenResponse.data;

      if (tokenData.status == 0 || tokenData.status == 200) {
        const protectedUrl: ProtectedUrl = tokenData.payload;
        window.location.href = `${siteProps.topUrl}${protectedUrl.urlFragment}`;
      } else {
        loggedNotify.negative(
          axiosContext,
          Component.FILES,
          "Unable to download file" +
            (tokenData.message.length > 0 ? ": " + tokenData.message : "")
        );
      }
    } catch (error) {
      loggedNotify.negative(
        axiosContext,
        Component.FILES,
        `Could not get secure download token for file ${fileId}: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }, [fileId, axiosContext, siteProps.topUrl]);

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      closeable
      animate
      autoFocus
      size="default"
      role="dialog"
      overrides={{
        Dialog: {
          style: {
            backgroundColor: currentTheme.colors.pageBackgroundColor,
            width: "50%",
          },
        },
        Close: {
          style: {
            color: currentTheme.colors.text,
          },
        },
      }}
    >
      <ModalHeader
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingRight: "48px",
        }}
      >
        <span
          style={{
            color: currentTheme.colors.fileModalHeaderTextColor,
            fontFamily: currentTheme.fonts.primary,
          }}
        >
          {header}
        </span>
        {fileId && (
          <StatefulTooltip
            placement={PLACEMENT.top}
            content={"Download file"}
            overrides={getTooltipOverrides(currentTheme)}
          >
            <Button
              kind="primary"
              size="compact"
              onClick={handleDownload}
              overrides={{
                BaseButton: {
                  style: {
                    color: currentTheme.colors.text,
                    backgroundColor: currentTheme.colors.backgroundTertiary,
                    ":hover": {
                      backgroundColor: currentTheme.colors.tabHoverColor,
                    },
                  },
                },
              }}
            >
              <FaDownload />
            </Button>
          </StatefulTooltip>
        )}
      </ModalHeader>
      <ModalBody style={{ maxHeight: "500px", overflow: "auto" }}>
        {!loading && !error && contentToShow && (
          <FileContentPreview fileType={fileType} content={contentToShow} />
        )}
      </ModalBody>
    </Modal>
  );
};
